import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePublishedPage } from '../hooks/usePublishedPage';
import { usePageSEO } from '../hooks/usePageSEO';
import { useContent } from '../context/ContentContext';
import { GOOGLE_RATING } from '../lib/defaultContent';
import type { PpcLandingContent } from '../types/cmsPages';
import { PhoneIcon, CheckCircleIcon, ShieldCheckIcon, StarIcon, ClockIcon } from '@heroicons/react/24/solid';
import veteranBadge from '../assets/optimized/veteranowned.webp';

export default function PpcLandingPage() {
  const { settings } = useContent();
  const { page, loading, notFound } = usePublishedPage('ppc_landing');
  usePageSEO();
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <h1 className="text-3xl font-serif font-bold text-brand-navy mb-4">Page Not Found</h1>
        <p className="text-gray-500 mb-6">This page doesn't exist or hasn't been published yet.</p>
        <Link to="/" className="text-brand-orange font-semibold hover:text-brand-navy transition-colors">
          Back to Home
        </Link>
      </div>
    );
  }

  const content = page.content as PpcLandingContent;
  const phone = content.cta_phone || settings.phone;

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
  };

  return (
    <div className="min-h-screen">
      <section className="relative bg-brand-navy overflow-hidden">
        {content.hero_image && (
          <div className="absolute inset-0">
            <img decoding="async" loading="lazy" src={content.hero_image} alt="" className="w-full h-full object-cover opacity-15" />
            <div className="absolute inset-0 bg-brand-navy/85" />
          </div>
        )}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-tan/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-brand-orange/10 border border-brand-orange/30 rounded-full px-4 py-2 mb-6">
                <ClockIcon className="w-4 h-4 text-brand-orange" />
                <span className="text-brand-orange font-semibold text-sm">Same-Day Service Available</span>
              </div>

              <h1 className="text-[32px] leading-[1.1] sm:text-[40px] md:text-[48px] lg:text-[52px] font-serif font-bold text-white mb-4 sm:mb-6">
                {content.headline}
              </h1>

              {content.subheadline && (
                <p className="text-lg sm:text-xl text-white/70 leading-relaxed mb-8">
                  {content.subheadline}
                </p>
              )}

              {content.bullets && content.bullets.length > 0 && (
                <div className="space-y-3 mb-8">
                  {content.bullets.map((bullet, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-brand-orange flex-shrink-0" />
                      <span className="text-white/90 font-medium">{bullet}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a
                  href={`tel:${phone.replace(/[^0-9]/g, '')}`}
                  className="inline-flex items-center justify-center gap-3 bg-brand-orange text-white px-8 py-5 rounded-xl font-bold text-lg hover:bg-white hover:text-brand-navy transition-all duration-300 shadow-lg shadow-brand-orange/25"
                >
                  <PhoneIcon className="w-6 h-6" />
                  Call {phone}
                </a>
              </div>

              {content.show_trust_badges && (
                <div className="flex items-center gap-6 mt-8 pt-8 border-t border-white/10">
                  <img decoding="async" loading="lazy" width={200} height={176} src={veteranBadge} alt="Veteran Owned" className="h-14 w-auto" />
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <StarIcon key={i} className="w-4 h-4 text-brand-orange" />
                      ))}
                    </div>
                    <span className="text-white/50 text-xs">{GOOGLE_RATING} Google Rating</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ShieldCheckIcon className="w-5 h-5 text-brand-orange" />
                    <span className="text-white/50 text-xs">Licensed & Insured</span>
                  </div>
                </div>
              )}
            </div>

            {content.show_form && (
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl">
                {formSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircleIcon className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-serif font-bold text-brand-navy mb-2">Thank You!</h3>
                    <p className="text-gray-500">We'll be in touch shortly with your free estimate.</p>
                  </div>
                ) : (
                  <>
                    <div className="text-center mb-6">
                      <h3 className="text-xl sm:text-2xl font-serif font-bold text-brand-navy mb-2">
                        Get Your Free Estimate
                      </h3>
                      <p className="text-gray-500 text-sm">Fill out the form below and we'll call you back within the hour.</p>
                    </div>
                    <form onSubmit={handleFormSubmit} className="space-y-4">
                      <input
                        type="text"
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                        className="w-full px-4 py-3.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange outline-none text-sm"
                      />
                      <input
                        type="tel"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        required
                        className="w-full px-4 py-3.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange outline-none text-sm"
                      />
                      <input
                        type="email"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-3.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange outline-none text-sm"
                      />
                      <textarea
                        placeholder="Tell us about your project (optional)"
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-3.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange outline-none text-sm resize-none"
                      />
                      <button
                        type="submit"
                        className="w-full bg-brand-orange text-white py-4 rounded-lg font-bold text-base hover:bg-brand-navy transition-colors shadow-lg"
                      >
                        {content.cta_button_text || 'Get Your Free Estimate'}
                      </button>
                      <p className="text-center text-xs text-gray-400">
                        No spam. We respect your privacy.
                      </p>
                    </form>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {!content.show_form && (
        <section className="py-16 sm:py-20 bg-brand-off-white">
          <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-navy mb-4">
              Don't Wait - Call Now for Immediate Service
            </h2>
            <a
              href={`tel:${phone.replace(/[^0-9]/g, '')}`}
              className="inline-flex items-center gap-3 bg-brand-orange text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-brand-navy transition-all duration-300 shadow-lg"
            >
              <PhoneIcon className="w-6 h-6" />
              {phone}
            </a>
          </div>
        </section>
      )}
    </div>
  );
}
